import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { api } from "../api/rest";
import type { AIAskView, AISummary } from "../api/types";
import { keepPolling } from "../lib/ai";

/** Такт опроса запроса в работе: провайдеры отвечают за десятые доли секунды. */
const ASK_POLL_MS = 500;

/** Такт истории и сводки — как у соседних теневых журналов. */
const AI_REFETCH_MS = 15_000;

/** Окно сводки по умолчанию — неделя: выводы делаются по сотням ответов. */
export const AI_DEFAULT_HOURS = 24 * 7;

/**
 * Один запрос: опрашивается раз в полсекунды, пока не станет окончательным
 * (или пока не выйдет срок — тогда он сам станет no_response на сервере).
 */
export function useAIAsk(askId: string | null) {
  const startedAt = useRef<{ id: string | null; at: number }>({ id: null, at: 0 });
  if (startedAt.current.id !== askId) startedAt.current = { id: askId, at: Date.now() };
  return useQuery<AIAskView>({
    queryKey: ["ai-ask", askId],
    queryFn: () => api.aiAsk(askId!),
    enabled: askId !== null,
    refetchInterval: (q) =>
      keepPolling(q.state.data, startedAt.current.at, Date.now()) ? ASK_POLL_MS : false,
    retry: 1,
  });
}

/** Нажатие «Спросить ИИ». После ответа обновляется история рынка. */
export function useAskAI(conditionID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (budgetUSD: string) => api.askAI(conditionID, budgetUSD),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ai-asks", conditionID] }),
  });
}

/** История запросов по рынку. */
export function useAIAsks(conditionID: string) {
  return useQuery<{ asks: AIAskView[] }>({
    queryKey: ["ai-asks", conditionID],
    queryFn: () => api.aiAsks(conditionID, 20),
    refetchInterval: AI_REFETCH_MS,
    retry: 1,
  });
}

/** Сводка сравнения провайдеров. */
export function useAISummary(hours: number, minConfidence: number) {
  return useQuery<{ hours: number; summary: AISummary }>({
    queryKey: ["ai-summary", hours, minConfidence],
    queryFn: () => api.aiSummary(hours, minConfidence),
    refetchInterval: AI_REFETCH_MS,
    retry: 1,
  });
}
